import { useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useDebounce } from "use-debounce";

import ebiOfficeService from "api/ebiOfficeService";
import Loading from "components/Loading";
import { IEbiMailTheme } from "dto/Ebi/IEbiOffice";
import { IError } from "dto/IError";
import { sameMailTheme } from "utils/ebiMailTheme";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import EbiMailThemeSection from "./EbiMailThemeSection";
import styles from "./styles.module.scss";
import { FC } from "./typings";

// Podzakładka „Powiadomienia”: lista szablonów maili EBI (z BE — jedno źródło prawdy o tym, co urząd wysyła), kolorystyka
// maili (gotowe warianty albo własne kolory) i podgląd wybranego szablonu z danymi urzędu (nazwa, adres, herb z zakładki
// Urząd), przykładowymi datami i kolorami z formularza — także niezapisanymi (podgląd dostaje je w zapytaniu, z opóźnieniem,
// żeby każdy ruch próbnika nie wołał serwera). HTML maila renderuje odizolowana ramka (sandbox bez skryptów); herb jest
// w nim wklejony jako data:, więc podgląd nie zależy od adresu publicznego serwera.

const EbiNotificationsTab: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, error, isLoading } = ebiOfficeService.useGetMailTemplates();
  const templates = Array.isArray(data?.data) ? data.data : [];

  const [selected, setSelected] = useState<string | null>(null);
  const activeKey = selected ?? templates[0]?.Key ?? null;

  const { data: themeData, error: themeError } = ebiOfficeService.useGetMailTheme();
  const themeSettings = themeData?.data;
  const savedTheme = themeSettings?.Theme ?? null;

  // kolory z formularza; null = jeszcze nie ruszone (podgląd i pola pokazują zapisane)
  const [draft, setDraft] = useState<IEbiMailTheme | null>(null);
  const dirty = !!draft && !sameMailTheme(draft, savedTheme);
  const [previewTheme] = useDebounce(dirty ? draft : null, 300);

  const {
    data: previewData,
    error: previewError,
    isFetching: isPreviewFetching,
  } = ebiOfficeService.useGetMailPreview(activeKey, dirty ? previewTheme : null);
  const preview = previewData?.data;

  const { mutate: saveTheme, isLoading: isSavingTheme } = ebiOfficeService.useSaveMailTheme();

  // jak w sekcji usług przeszyfrowań: odpowiedź bez komunikatu = serwer bez tej trasy
  const errorText = error ? getErrorMessage(error) || t("ebiOffice.notifications.unsupported") : null;
  const previewErrorText = previewError
    ? getErrorMessage(previewError) || t("ebiOffice.notifications.unsupported")
    : null;
  const themeErrorText = themeError
    ? getErrorMessage(themeError) || t("ebiOffice.notifications.theme.unsupported")
    : null;

  const recipientLabel = (recipient: string) =>
    t(`ebiOffice.notifications.recipient.${recipient}`, { defaultValue: recipient });

  const notify = (type: ENotificationType, text: string) =>
    notification({ type, title: t("ebiOffice.notifications.theme.title"), text });

  const handleSaveTheme = () => {
    if (!draft) return;

    saveTheme(draft, {
      onSuccess: ({ data: saved }) => {
        queryClient.setQueryData(ebiOfficeService.queryKeys.getMailTheme, { data: saved });
        setDraft(null);
        // podglądy z zapisanymi kolorami (bez kolorów w kluczu) są już nieaktualne
        queryClient.invalidateQueries("ebiOfficeService.getMailPreview");
        notify(ENotificationType.SUCCESS, t("ebiOffice.notifications.theme.saved"));
      },
      onError: (e) =>
        notify(
          ENotificationType.ERROR,
          getErrorMessage(e as IError) ||
            (e instanceof Error ? e.message : "") ||
            t("errors.somethingWentWrongShort"),
        ),
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.wide}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("ebiOffice.notifications.title")}</div>
        <div className={styles.hint}>{t("ebiOffice.notifications.description")}</div>
        {errorText && <div className={styles.error}>{errorText}</div>}
      </div>

      <div className={styles.notifications}>
        <div className={styles.templateList}>
          {templates.length > 0 && (
            <div className={styles.templates}>
              {templates.map((template) => (
                <div
                  key={template.Key}
                  className={classNames(styles.template, template.Key === activeKey && styles.templateActive)}
                  onClick={() => setSelected(template.Key)}
                >
                  <div className={styles.templateName}>{template.Name}</div>
                  <div className={styles.hint}>{recipientLabel(template.Recipient)}</div>
                  <div className={styles.hint}>{template.Description}</div>
                </div>
              ))}
            </div>
          )}

          <EbiMailThemeSection
            settings={themeSettings}
            value={draft ?? savedTheme}
            dirty={dirty}
            isSaving={isSavingTheme}
            error={themeErrorText}
            onChange={setDraft}
            onSave={handleSaveTheme}
            onCancel={() => setDraft(null)}
          />
        </div>

        {templates.length > 0 && (
          <div className={styles.previewBox}>
            {previewErrorText && <div className={styles.error}>{previewErrorText}</div>}
            {!preview && !previewErrorText && (
              <div className={styles.hint}>
                {isPreviewFetching ? <Loading /> : t("ebiOffice.notifications.selectHint")}
              </div>
            )}
            {preview && (
              <>
                <div className={styles.previewMeta}>
                  <span className={styles.previewLabel}>{t("ebiOffice.notifications.subject")}:</span>{" "}
                  {preview.Subject}
                </div>
                {preview.Preheader && (
                  <div className={styles.previewMeta}>
                    <span className={styles.previewLabel}>{t("ebiOffice.notifications.preheader")}:</span>{" "}
                    {preview.Preheader}
                  </div>
                )}
                {!preview.HasEmblem && (
                  <div className={styles.hint}>{t("ebiOffice.notifications.noEmblem")}</div>
                )}
                <iframe
                  className={styles.previewFrame}
                  title={`${t("ebiOffice.notifications.previewTitle")}: ${preview.Name}`}
                  sandbox=""
                  srcDoc={preview.Html}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EbiNotificationsTab;
